import React, { useState, useEffect, useRef } from 'react';

export default function InstagramStyleChat({
  messages,
  onSendMessage,
  userName,
  otherUserName,
  isParent,
  onAudioCall,
  onVideoCall
}) {
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef(null);
  const messageListRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (messageInput.trim()) {
      onSendMessage(messageInput);
      setMessageInput('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  return (
    <div className="instagram-chat-container">
      {/* Chat Header */}
      <div className="instagram-chat-header">
        <div className="chat-header-left">
          <div className="chat-avatar">
            {otherUserName?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="chat-header-info">
            <div className="chat-header-name">{otherUserName || 'Chat'}</div>
            <div className="chat-header-status">
              <span className="status-dot"></span>
              {isParent ? 'Child Device' : 'Parent Account'}
            </div>
          </div>
        </div>
        <div className="chat-header-actions">
          <button className="chat-header-btn" title="Audio Call" onClick={onAudioCall}>📞</button>
          <button className="chat-header-btn" title="Video Call" onClick={onVideoCall}>🎥</button>
          <button className="chat-header-btn" title="Info">ℹ️</button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="instagram-chat-messages" ref={messageListRef}>
        {messages && messages.length === 0 ? (
          <div className="instagram-empty-state">
            <div className="empty-state-icon">💬</div>
            <div className="empty-state-text">No messages yet</div>
            <div className="empty-state-subtext">
              Start a conversation with {otherUserName || 'the other person'}
            </div>
          </div>
        ) : (
          messages?.map((msg, idx) => {
            const isOwnMessage = isParent ? !msg.fromChild : !!msg.fromChild;
            return (
              <div
                key={idx}
                className={`instagram-message-wrapper ${isOwnMessage ? 'self' : 'peer'}`}
              >
                <div className="instagram-message">
                  <div className="message-bubble" title={formatTime(msg.timestamp)}>
                    {msg.message}
                  </div>
                  <div className="message-timestamp">
                    {formatTime(msg.timestamp)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Instagram Style */}
      <div className="instagram-chat-input-area">
        <div className="input-wrapper">
          <button className="input-btn" title="Add media">➕</button>
          <input
            type="text"
            className="instagram-chat-input"
            placeholder="Aa"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          {messageInput.trim() ? (
            <button className="input-btn send-btn" onClick={handleSend} title="Send">
              ❤️
            </button>
          ) : (
            <button className="input-btn" title="Like">👍</button>
          )}
        </div>
      </div>

      <style>{`
        .instagram-chat-container {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: var(--bg-card);
          border-radius: 16px;
          overflow: hidden;
        }

        /* ─── Header ─────────────────────────────────────── */
        .instagram-chat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border);
          background: linear-gradient(135deg, rgba(17,24,39,0.8), rgba(13,21,40,0.8));
          backdrop-filter: blur(10px);
        }

        .chat-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .chat-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--gradient-blue);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 16px;
          color: white;
        }

        .chat-header-info {
          flex: 1;
        }

        .chat-header-name {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .chat-header-status {
          font-size: 12px;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 2px;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          background: var(--green);
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .chat-header-actions {
          display: flex;
          gap: 12px;
        }

        .chat-header-btn {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .chat-header-btn:hover {
          opacity: 1;
        }

        /* ─── Messages Area ──────────────────────────────── */
        .instagram-chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          scroll-behavior: smooth;
        }

        .instagram-chat-messages::-webkit-scrollbar {
          width: 4px;
        }

        .instagram-chat-messages::-webkit-scrollbar-track {
          background: transparent;
        }

        .instagram-chat-messages::-webkit-scrollbar-thumb {
          background: var(--border-active);
          border-radius: 2px;
        }

        .instagram-message-wrapper {
          display: flex;
          margin-bottom: 4px;
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .instagram-message-wrapper.peer {
          justify-content: flex-start;
        }

        .instagram-message-wrapper.self {
          justify-content: flex-end;
        }

        .instagram-message {
          display: flex;
          flex-direction: column;
          gap: 4px;
          max-width: 60%;
        }

        .instagram-message-wrapper.self .instagram-message {
          align-items: flex-end;
        }

        .message-bubble {
          background: var(--bg-secondary);
          color: var(--text-primary);
          padding: 12px 16px;
          border-radius: 20px;
          word-wrap: break-word;
          line-height: 1.4;
          font-size: 14px;
          max-width: 100%;
          border: 1px solid var(--border);
          transition: background 0.2s;
        }

        .instagram-message-wrapper.self .message-bubble {
          background: var(--gradient-blue);
          border: 1px solid rgba(59, 130, 246, 0.5);
          color: white;
        }

        .message-bubble:hover {
          background: var(--bg-card-hover);
        }

        .instagram-message-wrapper.self .message-bubble:hover {
          background: var(--blue-light);
        }

        .message-timestamp {
          font-size: 11px;
          color: var(--text-muted);
          padding: 0 4px;
        }

        .instagram-empty-state {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          height: 100%;
          color: var(--text-secondary);
          text-align: center;
        }

        .empty-state-icon {
          font-size: 48px;
          margin-bottom: 16px;
          opacity: 0.6;
        }

        .empty-state-text {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
          color: var(--text-primary);
        }

        .empty-state-subtext {
          font-size: 13px;
          color: var(--text-secondary);
        }

        /* ─── Input Area ─────────────────────────────────── */
        .instagram-chat-input-area {
          padding: 12px 16px 20px;
          background: linear-gradient(180deg, transparent, rgba(13,21,40,0.5));
          border-top: 1px solid var(--border);
        }

        .input-wrapper {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          background: var(--bg-secondary);
          border: 1px solid var(--border);
          border-radius: 24px;
          padding: 8px 12px;
          transition: border-color 0.2s;
        }

        .input-wrapper:focus-within {
          border-color: var(--border-active);
        }

        .input-btn {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          padding: 6px;
          opacity: 0.7;
          transition: opacity 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 32px;
        }

        .input-btn:hover {
          opacity: 1;
        }

        .input-btn.send-btn {
          opacity: 1;
          animation: heartBeat 0.4s ease-in-out;
        }

        @keyframes heartBeat {
          0% { transform: scale(1); }
          25% { transform: scale(1.3); }
          50% { transform: scale(1); }
        }

        .instagram-chat-input {
          flex: 1;
          background: transparent;
          border: none;
          color: var(--text-primary);
          font-size: 14px;
          outline: none;
          padding: 8px 4px;
          font-family: inherit;
          max-height: 100px;
          resize: none;
        }

        .instagram-chat-input::placeholder {
          color: var(--text-muted);
        }

        @media (max-width: 768px) {
          .instagram-message {
            max-width: 80%;
          }

          .message-bubble {
            padding: 10px 14px;
            font-size: 13px;
          }

          .instagram-chat-header {
            padding: 12px 16px;
          }

          .chat-avatar {
            width: 36px;
            height: 36px;
            font-size: 14px;
          }

          .chat-header-name {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
}
